import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { base44 } from "@/api/base44Client";
import { ShoppingCart, Star, CheckCircle, Zap, Clock, Target, Shield, Wrench, Siren, Home as HomeIcon, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/components/LanguageContext";
import { useRegion } from "@/components/RegionContext";
import LazySection from "@/components/LazySection";
import { trackViewContent, trackAddToCart } from "@/lib/pixel";

// Responsive image URLs - optimized for performance
const getProductImage = (index, isMobile) => {
  const imageIds = [
    "1_iatrwm.jpg",
    "2_wuef4t.jpg",
    "3_dsotbs.jpg",
    "5_gjicgv.jpg",
    "6_zril2w.jpg",
    "7_cn0emj.jpg",
    "%E7%94%BB%E6%9D%BF_2_twgryo.jpg"
  ];
  // Smaller images for better performance - 400px mobile, 600px desktop
  const width = isMobile ? 400 : 600;
  return `https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_30,w_${width},c_limit/v1759783025/${imageIds[index]}`;
};

export default function HomePage() {
  const { t, language } = useLanguage();
  const { formatLocalPrice, currencyCode } = useRegion();
  const [selectedImage, setSelectedImage] = useState(0);
  // Initialize synchronously so the first render already uses the right image
  // size (no desktop→mobile URL swap = no double download).
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    
    // Lazy load non-critical resources after page load
    if (document.readyState === 'complete') {
      // Page already loaded
      setTimeout(() => {
        // Prefetch next likely navigation
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = createPageUrl("Product");
        document.head.appendChild(link);
      }, 2000);
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = createPageUrl("Product");
          document.head.appendChild(link);
        }, 2000);
      }, { once: true });
    }
  }, []);

  // Arabic-localized product gallery (uploaded AR infographics 1-7)
  // Served through the wsrv.nl image CDN: resized + WebP instead of the raw
  // ~180KB-per-image originals (7 images ≈ 1.2MB → ~150KB total).
  const arabicImageOriginals = [
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/de52380ab_1.jpg",
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/a28dddb27_2.jpg",
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/b0655caf3_3.jpg",
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/d7672a249_4.jpg",
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/fe156cf22_5.jpg",
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/05a3379aa_6.jpg",
    "https://media.base44.com/images/public/68e21bb4c1e25d4ab005735e/79804181c_7.jpg",
  ];
  const resizeImage = (url, width) =>
    `https://wsrv.nl/?url=${encodeURIComponent(url.replace(/^https?:\/\//, ""))}&w=${width}&q=70&output=webp`;
  const productImages = language === "ar"
    ? arabicImageOriginals.map((u) => resizeImage(u, isMobile ? 480 : 800))
    : Array.from({ length: 7 }, (_, i) => getProductImage(i, isMobile));
  // Tiny variants for the 48px thumbnail strip (EN reuses the already-cached main URLs)
  const productThumbs = language === "ar"
    ? arabicImageOriginals.map((u) => resizeImage(u, 96))
    : productImages;

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  useEffect(() => {
    // Preload first image with optimized settings
    const isMobileDevice = window.innerWidth < 768;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.fetchPriority = 'high';
    link.href = productImages[0];
    document.head.appendChild(link);

    // Preload critical Cloudinary domain
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://res.cloudinary.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    document.title = "LCTX Tire Changer $399 | Portable Manual Tire Changer Machine | Air-Powered Tyre Changer | Free Worldwide Shipping";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = "LCTX Portable Tire Changer Machine $399 USD - Professional manual tire changer for mechanics. Air-powered, no electricity needed. 12-21\" wheels, 2 ton torque. Free worldwide shipping. Buy now!";
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }

    // Keywords meta tag (still useful for some search engines)
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = "tire changer, tire changer machine, portable tire changer, manual tire changer, easy tire changer, ez tire changer, air powered tire changer, pneumatic tire changer, tire changing machine, tyre changer, mobile tire changer, tire changer for sale, best tire changer, professional tire changer, tire changer tool, tire mounting machine, tire removal tool, LCTX tire changer, cheap tire changer, tire changer price, tire changer portable, motorcycle tire changer";

    // Enhanced Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'LCTX Portable Tire Changer Machine | $399 USD Air-Powered Tyre Changer' },
      { property: 'og:description', content: 'Professional portable tire changer machine - No electricity needed! 2 ton torque, handles 12-21" wheels. Only $399 USD with free worldwide shipping.' },
      { property: 'og:type', content: 'product' },
      { property: 'og:url', content: window.location.href },
      { property: 'og:image', content: 'https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_auto,w_1200/v1759783025/1_iatrwm.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:site_name', content: 'LCTX' },
      { property: 'product:price:amount', content: '399' },
      { property: 'product:price:currency', content: 'USD' }
    ];

    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.content = tag.content;
    });

    // Enhanced Structured Data (Product Schema)
    let structuredData = document.querySelector('script[type="application/ld+json"]#product-schema');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.type = 'application/ld+json';
      structuredData.id = 'product-schema';
      document.head.appendChild(structuredData);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "LCTX Air-Powered Portable Tire Changer Machine",
      "image": [
        "https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_auto,w_1200/v1759783025/1_iatrwm.jpg",
        "https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_auto,w_1200/v1759783029/2_wuef4t.jpg",
        "https://res.cloudinary.com/dilx8zm2p/image/upload/f_auto,q_auto,w_1200/v1759783027/3_dsotbs.jpg"
      ],
      "description": "Professional portable pneumatic tire changer machine. No electricity required - air-powered design. Handles 12-21 inch wheels with 2 ton bead-breaking torque. Perfect for mobile mechanics, auto shops, and DIY. Free shipping across the UAE and Saudi Arabia.",
      "brand": {
        "@type": "Brand",
        "name": "LCTX"
      },
      "sku": "LCTX-TC-2024",
      "mpn": "LCTX-TIRE-CHANGER-PRO",
      "offers": {
        "@type": "Offer",
        "url": window.location.origin + createPageUrl("Product"),
        "priceCurrency": "USD",
        "price": "399",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition",
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": "0",
            "currency": "USD"
          },
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "Worldwide"
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 1,
              "maxValue": 2,
              "unitCode": "DAY"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 3,
              "maxValue": 10,
              "unitCode": "DAY"
            }
          }
        },
        "seller": {
          "@type": "Organization",
          "name": "LCTX",
          "url": window.location.origin
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "500",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Mobile Mechanic Mike"
          },
          "reviewBody": "Best portable tire changer I've ever used! No electricity needed, super portable. Changed 20+ tires without any issues. Highly recommend for mobile mechanics!"
        },
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Sarah from Dubai"
          },
          "reviewBody": "Excellent quality and very easy to use. Saved me hundreds of dollars compared to going to a tire shop. Worth every penny!"
        }
      ],
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Wheel Size Range",
          "value": "12-21 inches"
        },
        {
          "@type": "PropertyValue",
          "name": "Maximum Wheel Diameter",
          "value": "39 inches"
        },
        {
          "@type": "PropertyValue",
          "name": "Bead Breaker Force",
          "value": "2000 kg"
        },
        {
          "@type": "PropertyValue",
          "name": "Weight",
          "value": "36 kg"
        },
        {
          "@type": "PropertyValue",
          "name": "Power Source",
          "value": "Pneumatic (Air-Powered)"
        }
      ]
    };

    structuredData.textContent = JSON.stringify(schema);

    // Add FAQ Schema
    let faqSchema = document.querySelector('script[type="application/ld+json"]#faq-schema');
    if (!faqSchema) {
      faqSchema = document.createElement('script');
      faqSchema.type = 'application/ld+json';
      faqSchema.id = 'faq-schema';
      document.head.appendChild(faqSchema);
    }

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Does this tire changer require electricity?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No! The LCTX Tire Changer is 100% air-powered. You only need an air compressor with 8-10 Bar pressure. No electricity needed, making it perfect for mobile mechanics and outdoor use."
          }
        },
        {
          "@type": "Question",
          "name": "What tire sizes can the LCTX tire changer handle?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The LCTX can handle tire sizes from 12 to 21 inch rim diameter, covering most passenger cars, SUVs, and light trucks. Maximum wheel diameter is 39 inches."
          }
        },
        {
          "@type": "Question",
          "name": "How much does the LCTX tire changer cost?",
          "acceptedAnswer": {
          "@type": "Answer",
          "text": "The LCTX Portable Tire Changer costs $399 (USD) - approximately AED 1,465.31 in the UAE or SAR 1,496.25 in Saudi Arabia (incl. VAT). Free shipping across the Middle East."
          }
        },
        {
          "@type": "Question",
          "name": "Is the LCTX tire changer portable?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! At only 36 kg (80 lbs), the LCTX is truly portable. Unlike traditional tire changers that weigh 300+ lbs, it can be easily transported in your vehicle."
          }
        }
      ]
    };

    faqSchema.textContent = JSON.stringify(faqData);

    // Meta conversion signal: ad traffic landed on the product landing page.
    trackViewContent({ source: "home" });
  }, []);

  return (
    <div className="bg-gradient-to-br from-pink-50 to-white dark:from-gray-900 dark:to-gray-950 min-h-screen w-full overflow-x-hidden">

      {/* Hero Product Section */}
      <section className="py-3 md:py-8 w-full hero-section" style={{ minHeight: '600px' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full">
          {/* SEO-optimized H1 */}
          <h1 className="sr-only">LCTX Portable Tire Changer Machine | AED 1,465 / SAR 1,496 Professional Manual Air-Powered Tyre Changer | No Electricity Needed | Free Shipping Across UAE & Saudi Arabia | 12-21" Wheels | 2 Ton Torque | Mobile Mechanic Tire Changing Tool</h1>
          
          <div className="grid lg:grid-cols-2 gap-3 md:gap-8 items-start w-full">

            {/* Product Images - Left Side */}
            <div className="relative w-full">
              <div className="sticky top-20">
                <div className="relative bg-white rounded-lg overflow-hidden shadow-lg mb-2 w-full min-h-[300px] md:min-h-[560px]" style={{ aspectRatio: '1 / 1' }}>
                  {/* BESTSELLER Badge */}
                  <div className="absolute top-2 left-2 z-10">
                    <Badge className="bg-red-700 hover:bg-red-700 text-white px-2 py-0.5 text-[9px] font-bold uppercase">
                      BESTSELLER
                    </Badge>
                  </div>

                  <img
                    src={productImages[selectedImage]}
                    alt={`LCTX Tyre Changer - View ${selectedImage + 1}`}
                    className="block w-full h-auto object-contain aspect-square max-h-[80vh] md:max-h-[560px]"
                    width={isMobile ? "400" : "600"}
                    height={isMobile ? "400" : "600"}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />

                  <button
                    onClick={prevImage}
                    className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-lg transition-all z-10"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-lg transition-all z-10"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-0.5 rounded-full text-[10px] z-10">
                    {selectedImage + 1}/{productImages.length}
                  </div>
                </div>

                <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide w-full">
                  {productImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                        selectedImage === index
                          ? "border-red-600 ring-1 ring-red-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img 
                        src={productThumbs[index]}
                        alt={`Thumbnail ${index + 1}`} 
                        className="w-full h-full object-cover"
                        width="48"
                        height="48"
                        loading="lazy"
                        decoding="async"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Product Info - Right Side */}
            <div className="space-y-2 md:space-y-4 w-full" style={{ minHeight: '600px' }}>
              {/* Slogan Badge */}
              <div className="inline-flex items-center gap-1 md:gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-2 md:px-4 py-1 md:py-2 rounded-full shadow-lg">
                <span className="text-[10px] md:text-sm font-bold break-words">{t("home_slogan")}</span>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2" style={{ minHeight: '20px' }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 md:w-5 md:h-5 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="text-[10px] md:text-sm text-gray-600">{t("home_reviews")}</span>
              </div>

              {/* Title */}
              <div>
                <h2 className="text-xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-2 md:mb-4 leading-tight break-words">
                  {t("home_title")}
                </h2>
                <p className="text-sm md:text-lg text-red-600 dark:text-red-500 font-bold break-words">
                  {t("home_subtitle")}
                </p>
              </div>

              {/* Description */}
              <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 break-words">
                {t("home_desc")}
              </p>

              {/* Feature Grid */}
              <div className="grid grid-cols-2 gap-2 md:gap-4 py-2 md:py-4 w-full" style={{ minHeight: '120px' }}>
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="flex-shrink-0">
                    <Zap className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-[10px] md:text-sm break-words">{t("home_feat_torque_t")}</div>
                    <div className="text-[8px] md:text-xs text-gray-600 break-words">{t("home_feat_torque_s")}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 md:gap-3">
                  <div className="flex-shrink-0">
                    <Clock className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-[10px] md:text-sm break-words">{t("home_feat_time_t")}</div>
                    <div className="text-[8px] md:text-xs text-gray-600 break-words">{t("home_feat_time_s")}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 md:gap-3">
                  <div className="flex-shrink-0">
                    <Target className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-[10px] md:text-sm break-words">{t("home_feat_prec_t")}</div>
                    <div className="text-[8px] md:text-xs text-gray-600 break-words">{t("home_feat_prec_s")}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 md:gap-3">
                  <div className="flex-shrink-0">
                    <Shield className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-[10px] md:text-sm break-words">{t("home_feat_dur_t")}</div>
                    <div className="text-[8px] md:text-xs text-gray-600 break-words">{t("home_feat_dur_s")}</div>
                  </div>
                </div>
              </div>

              {/* Price */}
              <div className="space-y-1 md:space-y-2 w-full">
                <div className="flex items-baseline gap-2 md:gap-3 flex-wrap">
                  <span className="text-xl md:text-3xl font-bold text-gray-400 line-through">
                    {formatLocalPrice(699.99)}
                  </span>
                  <span className="text-3xl md:text-5xl font-black text-red-600">
                    {formatLocalPrice(399)}
                  </span>
                  <Badge className="bg-red-600 hover:bg-red-600 text-white px-2 md:px-3 py-0.5 md:py-1 text-[9px] md:text-xs font-bold">
                    {t("home_save")}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-gray-600 font-semibold">
                  {currencyCode}
                </p>

                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0" />
                  <span className="text-green-600 font-semibold text-[10px] md:text-sm break-words">{t("home_instock")}</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-2 md:space-y-3 pt-2 w-full">
                <Link to={createPageUrl("Product")} className="block w-full">
                    <Button
                      onClick={() => {
                        base44.analytics.track({
                          eventName: "ez_tire_changer_order_now_clicked",
                          properties: { source: "home_hero" }
                        });
                        trackAddToCart({ source: "home_hero" });
                      }}
                      className="w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white h-12 md:h-14 text-sm md:text-lg font-bold rounded-lg shadow-lg select-none transition-transform duration-100 active:scale-[0.97]"
                    >
                      <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0 select-none" />
                      <span className="truncate">{t("order_now")}</span>
                    </Button>
                </Link>
                <a 
                  href="https://www.youtube.com/@Eztirechanger?sub_confirmation=1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full"
                  onClick={() => {
                    base44.analytics.track({
                      eventName: "educational_content_clicked",
                      properties: { 
                        content_type: "youtube_subscribe",
                        location: "home_hero"
                      }
                    });
                  }}
                >
                  <Button variant="outline" className="w-full h-10 md:h-12 text-xs md:text-base font-semibold border-2">
                    <Play className="w-3 h-3 md:w-4 md:h-4 mr-2 flex-shrink-0" />
                    {t("home_subscribe")}
                  </Button>
                </a>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-3 md:gap-6 pt-2 md:pt-4 border-t text-[9px] md:text-xs text-gray-600 flex-wrap">
                <div className="flex items-center gap-1">
                  <span>🔒</span>
                  <span className="whitespace-nowrap">{t("home_trust_secure")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>📦</span>
                  <span className="whitespace-nowrap">{t("home_trust_ships")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>✅</span>
                  <span className="whitespace-nowrap">{t("home_trust_warranty")}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3D Product Viewer - TEMPORARILY DISABLED TO FIX LOADING ISSUES */}
      {/* <React.Suspense fallback={
        <div className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-red-600 mx-auto"></div>
        </div>
      }>
        <Product3DViewer />
      </React.Suspense> */}

      {/* SEO Content Section - NEW (deferred: rendered when scrolled near) */}
      <LazySection rootMargin="600px" fallback={<div style={{ minHeight: '1200px' }} />}>
      <section className="py-16 bg-gray-50" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 1200px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-black text-gray-900 mb-8">{t("home_seo_heading")}</h2>
            
            <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
              <p>
                {t("home_seo_p1")}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_1")}</h3>
              <p>
                {t("home_seo_p2")}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_2")}</h3>
              <p>
                {t("home_seo_p3")}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_3")}</h3>
              <p>
                {t("home_seo_p4")}
              </p>

              <h3 className="2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_4")}</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>{t("home_seo_li_1")}</li>
                <li>{t("home_seo_li_2")}</li>
                <li>{t("home_seo_li_3")}</li>
                <li>{t("home_seo_li_4")}</li>
                <li>{t("home_seo_li_5")}</li>
                <li>{t("home_seo_li_6")}</li>
                <li>{t("home_seo_li_7")}</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_5")}</h3>
              <p>
                {t("home_seo_p5")}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_6")}</h3>
              <p>
                {t("home_seo_p6")}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_7")}</h3>
              <p>
                {t("home_seo_p7")}
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t("home_seo_h2_8")}</h3>
              <p>
                {t("home_seo_p8")}
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
                <h4 className="text-xl font-bold text-blue-900 mb-2">{t("home_seo_box_h")}</h4>
                <p className="text-blue-800 mb-4">
                  {t("home_seo_box_p")}
                </p>
                <Link to={createPageUrl("Product")}>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    {t("home_seo_box_btn")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      </LazySection>

      <LazySection rootMargin="600px" fallback={<div style={{ minHeight: '800px' }} />}>
      <section className="py-16 bg-white dark:bg-gray-950" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 800px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white mb-4">
              {t("home_why_heading")}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
              {t("home_why_sub")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-4">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Wrench className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2">{t("home_card1_t")}</h3>
              <p className="text-xs md:text-base text-gray-600">{t("home_card1_d")}</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Siren className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2">{t("home_card2_t")}</h3>
              <p className="text-xs md:text-base text-gray-600">{t("home_card2_d")}</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                <HomeIcon className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2">{t("home_card3_t")}</h3>
              <p className="text-xs md:text-base text-gray-600">{t("home_card3_d")}</p>
            </div>
          </div>
        </div>
      </section>
      </LazySection>

      {/* Important Notice (deferred) */}
      <LazySection rootMargin="600px" fallback={<div style={{ minHeight: '260px' }} />}>
      <section className="py-12 bg-yellow-50" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 260px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-100 border-l-4 border-yellow-500 p-6 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">!</span>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base md:text-lg mb-2">
                  {t("home_notice_title")}
                </h3>
                <p className="text-sm md:text-base text-gray-700">
                  {t("home_notice_text")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      </LazySection>

      {/* FAQ Section (deferred) */}
      <LazySection rootMargin="600px" fallback={<div style={{ minHeight: '1000px' }} />}>
      <section id="faq" className="py-16 bg-gradient-to-br from-gray-50 to-white" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 1000px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-red-600 text-white px-3 py-2 rounded-full font-bold text-xs md:text-sm mb-6">
              {t("home_faq_badge")}
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4">
              {t("home_faq_heading")}
            </h2>
            <p className="text-base md:text-xl text-gray-600">
              {t("home_faq_sub")}
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { question: t("home_faq1_q"), answer: t("home_faq1_a"), id: "requires_electricity" },
              { question: t("home_faq2_q"), answer: t("home_faq2_a"), id: "tyre_sizes" },
              { question: t("home_faq3_q"), answer: t("home_faq3_a"), id: "portability" },
              { question: t("home_faq4_q"), answer: t("home_faq4_a"), id: "professional_use" },
              { question: t("home_faq5_q"), answer: t("home_faq5_a"), id: "package_contents" },
              { question: t("home_faq6_q"), answer: t("home_faq6_a"), id: "shipping_time" },
              { question: t("home_faq7_q"), answer: t("home_faq7_a"), id: "refund_policy" },
              { question: t("home_faq8_q"), answer: t("home_faq8_a"), id: "training_required" }
            ].map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-red-600 transition-all shadow-lg hover:shadow-xl"
              >
                <AccordionTrigger 
                  className="px-4 py-3 hover:no-underline group"
                  onClick={() => {
                    base44.analytics.track({
                      eventName: "faq_interaction",
                      properties: { 
                        question_id: faq.id,
                        question: faq.question
                      }
                    });
                  }}
                >
                  <div className="flex items-center gap-4 text-left">
                    <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors">
                      <span className="text-red-600 group-hover:text-white font-black text-base">Q</span>
                    </div>
                    <span className="text-base md:text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                      {faq.question}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="flex items-start gap-4 pt-4 border-t border-gray-100">
                    <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 font-black text-base">A</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                      {faq.answer}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 shadow-2xl">
            <h3 className="text-xl md:text-3xl font-black text-white mb-4">
              {t("home_still_q")}
            </h3>
            <p className="text-red-100 text-sm md:text-lg mb-6">
              {t("home_still_text")}
            </p>
            <Link to={createPageUrl("Contact")}>
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-5 py-3 text-sm md:text-lg font-bold shadow-xl">
                {t("home_contact_support")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </LazySection>

      {/* Final CTA — deferred until scrolled near */}
      <LazySection rootMargin="300px">
      <section className="py-16 bg-gradient-to-br from-gray-900 to-black text-white" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 420px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            {t("home_final_heading")}
          </h2>
          <p className="text-base md:text-xl text-gray-300 mb-8">
            {t("home_final_sub")}
          </p>

          <Link to={createPageUrl("Product")}>
            <Button 
              size="lg" 
              onClick={() => {
                base44.analytics.track({
                  eventName: "ez_tire_changer_order_now_clicked",
                  properties: { source: "home_bottom_cta" }
                });
                trackAddToCart({ source: "home_bottom_cta" });
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-5 text-lg font-bold rounded-full"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              {t("order_now")}
            </Button>
          </Link>

          <p className="mt-6 text-sm md:text-base text-gray-400">
            {t("home_final_footer")}
          </p>
        </div>
      </section>
      </LazySection>
    </div>
  );
}